import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, getCurrentFinancialYear } from "../../../../utils/commonUtil";
import EditPurchaseCost from "../../MyCashFlow/EditPurchaseCost";
interface IPurchaseCost {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  capitalImprovementPurchasePrice: number;
  solicitorPurchasePrice: number;
  stampDutyPurchasePrice: number;
  otherPurchasePrice: number;
  loanEstablishmentPurchasePrice: number;
  valualtionPurchasePrice: number;
  otherLoanPurchasePrice: number;
}

const PurchaseCost: React.FC<{
  cashFlowData: any;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  isShowingTotal?: boolean;
}> = ({ cashFlowData, year, isEditable, setIsEditable, isShowingTotal }) => {
  const [transformedData, setTransformedData] = useState<IPurchaseCost[]>([]);

  useEffect(() => {
    if (cashFlowData) {
      const actual = isShowingTotal
        ? cashFlowData.CashflowPurchaseCost?.Actuals?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPurchaseCostPortion?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecast = isShowingTotal
        ? cashFlowData.CashflowPurchaseCost?.Forecast?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPurchaseCostPortion?.Forecast?.find((item: any) => item.Year === year) || {};
      const variance = isShowingTotal
        ? cashFlowData.CashflowPurchaseCost?.Variance?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPurchaseCostPortion?.Variance?.find((item: any) => item.Year === year) || {};

      setTransformedData(
        [
          { type: "Actuals", data: actual },
          // { type: "Forecast", data: forecast },
          // { type: "Variance", data: variance },
        ].map(({ type, data }: { type: string; data: any }) => ({
          street: cashFlowData.PortfolioTrackerDetailLabel.StreetAddress || "",
          suburb: cashFlowData.PortfolioTrackerDetailLabel.Suburb || "",
          state: cashFlowData.PortfolioTrackerDetailLabel.State || "",
          postcode: cashFlowData.PortfolioTrackerDetailLabel?.Postcode || "",
          country: cashFlowData.PortfolioTrackerDetailLabel?.Country || "",
          type,
          capitalImprovementPurchasePrice: Number(data.Cost_CapitalImprovements) || 0,
          solicitorPurchasePrice: Number(data.Cost_SolicitorFees) || 0,
          stampDutyPurchasePrice: Number(data.Cost_StampDuty) || 0,
          otherPurchasePrice: Number(data.Cost_OtherCosts) || 0,
          loanEstablishmentPurchasePrice: Number(data.Loan_EstablishmentFees) || 0,
          valualtionPurchasePrice: Number(data.Cost_ValuationFees) || 0,
          otherLoanPurchasePrice: Number(data.Loan_OtherCost) || 0,
        }))
      );
    }
  }, [cashFlowData, year]);
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();
  const currencyTemplate = (value: number) =>
    Number(currentYear) < Number(year) ? (
      <span className="text-gray-500"></span>
    ) : (
      <span className={value < 0 ? "text-red-600" : ""}>
        {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
      </span>
    );

  return (
    <>
      {isEditable ? (
        <EditPurchaseCost
          cashFlowData={cashFlowData}
          setIsEditable={setIsEditable}
          onCancel={() => setIsEditable(false)}
          portfolioTrackerId={cashFlowData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
          propertyName={`${cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, ${cashFlowData.PortfolioTrackerDetailLabel.Suburb}, ${cashFlowData.PortfolioTrackerDetailLabel.State}, ${cashFlowData.PortfolioTrackerDetailLabel.Postcode} (${cashFlowData.PortfolioTrackerDetailLabel.Country})`}
        />
      ) : (
        transformedData && (
          <DataTable
            value={transformedData}
            rowGroupMode="rowspan"
            groupRowsBy="street"
            sortField="street"
            sortMode="single"
            sortOrder={1}
            size="small"
          >
            <Column
              field="street"
              header="Address/property name"
              body={(rowData) => (
                <div className="flex items-center">
                  <span>{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
                </div>
              )}
            />
            <Column field="type" header="Type" body={(rowData) => rowData.type} />
            <Column
              field="capitalImprovementPurchasePrice"
              header="Capital improvements at purchase"
              body={(rowData) => currencyTemplate(rowData.capitalImprovementPurchasePrice)}
              align="right"
            />
            <Column
              field="solicitorPurchasePrice"
              header="Solicitor costs"
              body={(rowData) => currencyTemplate(rowData.solicitorPurchasePrice)}
              align="right"
            />
            <Column
              field="stampDutyPurchasePrice"
              header="Stamp duty"
              body={(rowData) => currencyTemplate(rowData.stampDutyPurchasePrice)}
              align="right"
            />
            <Column
              field="otherPurchasePrice"
              header="Other purchase expenses"
              body={(rowData) => currencyTemplate(rowData.otherPurchasePrice)}
              align="right"
            />
            <Column
              field="loanEstablishmentPurchasePrice"
              header="Loan establishment fees"
              body={(rowData) => currencyTemplate(rowData.loanEstablishmentPurchasePrice)}
              align="right"
            />
            <Column
              field="valualtionPurchasePrice"
              header="Valuation fees"
              body={(rowData) => currencyTemplate(rowData.valualtionPurchasePrice)}
              align="right"
            />
            <Column
              field="otherLoanPurchasePrice"
              header="Other loan costs"
              body={(rowData) => currencyTemplate(rowData.otherLoanPurchasePrice)}
              align="right"
            />
          </DataTable>
        )
      )}
    </>
  );
};

export default PurchaseCost;
